/**
 * <em-reviews-marquee data-speed="1" data-pause-on-hover>
 * Continuously scrolling row of review cards. The markup holds the cards twice; the row is laid out
 * right-to-left and drifts towards the right, wrapping seamlessly after one full set. Cards can be
 * dragged. Scrolling stops while hovered (optional), off screen, in the theme editor, or when the
 * visitor prefers reduced motion.
 */

class EmReviewsMarquee extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.speed = parseFloat(this.dataset.speed || '1') || 1;
    this.offset = 0;
    this.isHovered = false;
    this.isVisible = false;
    this.drag = null;
    this.autoScroll = !window.Shopify?.designMode && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const viewport = this.parentElement;

    if (this.hasAttribute('data-pause-on-hover')) {
      viewport.addEventListener('mouseenter', () => (this.isHovered = true), { signal });
      viewport.addEventListener('mouseleave', () => (this.isHovered = false), { signal });
    }

    this.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
    document.addEventListener('pointermove', (event) => this.moveDrag(event), { signal });
    document.addEventListener('pointerup', () => this.endDrag(), { signal });
    document.addEventListener('pointercancel', () => this.endDrag(), { signal });
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);

    this.visibilityObserver = new IntersectionObserver((entries) => {
      this.isVisible = entries.some((entry) => entry.isIntersecting);
      if (this.isVisible) this.tick();
    });
    this.visibilityObserver.observe(viewport);

    this.measure();
    this.centerFirstCard();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  /** Width of one full set of cards (half of the rendered cards). */
  measure() {
    const cards = Array.from(this.children);
    const half = cards.length / 2;
    if (!half) {
      this.loopWidth = 0;
      return;
    }

    const first = cards[0].getBoundingClientRect();
    const firstClone = cards[half].getBoundingClientRect();
    this.loopWidth = Math.abs(first.left - firstClone.left);
    this.apply();
  }

  centerFirstCard() {
    const first = this.children[0];
    if (!first || !this.loopWidth) return;

    const viewportWidth = this.parentElement.clientWidth;
    this.offset = -(viewportWidth / 2 - first.getBoundingClientRect().width / 2);
    this.apply();
  }

  apply() {
    if (this.loopWidth > 0) {
      this.offset = ((this.offset % this.loopWidth) + this.loopWidth) % this.loopWidth;
    }
    this.style.transform = `translateX(${this.offset}px)`;
  }

  tick() {
    cancelAnimationFrame(this.frame);
    let last = performance.now();

    const step = (now) => {
      if (!this.isVisible || !this.isConnected) return;

      const elapsed = Math.min(64, now - last);
      last = now;

      if (this.autoScroll && !this.isHovered && !this.drag) {
        this.offset += this.speed * (elapsed / (1000 / 60));
        this.apply();
      }

      this.frame = requestAnimationFrame(step);
    };

    this.frame = requestAnimationFrame(step);
  }

  startDrag(event) {
    if (event.button !== undefined && event.button !== 0) return;
    this.drag = { x: event.clientX, offset: this.offset };
    this.classList.add('is-dragging');
  }

  moveDrag(event) {
    if (!this.drag) return;
    this.offset = this.drag.offset + (event.clientX - this.drag.x);
    this.apply();
  }

  endDrag() {
    if (!this.drag) return;
    this.drag = null;
    this.classList.remove('is-dragging');
  }
}

if (!customElements.get('em-reviews-marquee')) {
  customElements.define('em-reviews-marquee', EmReviewsMarquee);
}
